import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { existsSync, readdirSync, readFileSync } from 'node:fs'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { getDefaultUvPath } from '../provisioning/uv.js'
import { getVenvPythonPath, getPythonPath } from '../provisioning/jcodemunch.js'
import { getMcpRegistrationStatus } from '../registration/mcp.js'
import { COMMANDS_GM_DIR } from '../registration/commands.js'
import { AGENTS_DIR } from '../registration/agents.js'
import { getGsdMunchHome } from '../registration/assets.js'

const execFileAsync = promisify(execFile)

const noColor = process.env.NO_COLOR !== undefined

const GREEN = noColor ? '' : '\x1b[32m'
const RED = noColor ? '' : '\x1b[31m'
const YELLOW = noColor ? '' : '\x1b[33m'
const DIM = noColor ? '' : '\x1b[2m'
const RESET = noColor ? '' : '\x1b[0m'

const CHECK = noColor ? '[ok]' : `${GREEN}✓${RESET}`
const CROSS = noColor ? '[x]' : `${RED}✗${RESET}`
const WARN = noColor ? '[!]' : `${YELLOW}!${RESET}`

const REQUIRED_LANGUAGES = [
  'python', 'javascript', 'typescript', 'go', 'rust', 'java',
  'php', 'dart', 'c_sharp', 'c', 'cpp', 'elixir', 'ruby', 'sql',
]

export async function runDoctor(): Promise<void> {
  let passed = 0
  let total = 0

  console.log('gsd-munch doctor')
  console.log('')

  // Check 1: uv installed
  total++
  const uvPath = getDefaultUvPath()
  const uvExists = existsSync(uvPath)
  if (uvExists) {
    try {
      const { stdout } = await execFileAsync(uvPath, ['--version'], { timeout: 10_000 })
      const version = stdout.trim()
      console.log(`${CHECK} uv installed ... ${version}`)
      passed++
    } catch {
      console.log(`${CHECK} uv installed ... ${DIM}(version check failed)${RESET}`)
      passed++
    }
  } else {
    console.log(`${CROSS} uv installed ... not found at ${uvPath}`)
  }

  // Check 2: Python available
  total++
  if (uvExists) {
    try {
      await execFileAsync(uvPath, ['python', 'find', '3.10'], { timeout: 10_000 })
      console.log(`${CHECK} Python 3.10 available`)
      passed++
    } catch (err) {
      const msg = (err as NodeJS.ErrnoException).message ?? 'not found'
      console.log(`${CROSS} Python 3.10 available ... ${msg}`)
    }
  } else {
    console.log(`${CROSS} Python 3.10 available ... skipped (uv not found)`)
  }

  // Check 3: MCP registered (user scope)
  total++
  const mcpStatus = getMcpRegistrationStatus()
  if (mcpStatus.userScope) {
    console.log(`${CHECK} MCP registered (user scope)`)
    passed++
  } else {
    console.log(`${CROSS} MCP registered (user scope) ... not found in ~/.claude.json`)
  }

  // Check 4: MCP server starts (venv Python)
  total++
  const venvPython = getVenvPythonPath()
  const pythonModulePath = getPythonPath()
  const venvPythonExists = existsSync(venvPython)
  if (venvPythonExists) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 10_000)
    try {
      await execFileAsync(venvPython, ['-m', 'jcodemunch_mcp.server', '--help'], {
        signal: controller.signal,
        timeout: 10_000,
        env: { ...process.env, PYTHONPATH: pythonModulePath, JCODEMUNCH_USE_AI_SUMMARIES: 'false' },
      })
      clearTimeout(timer)
      console.log(`${CHECK} MCP server starts`)
      passed++
    } catch (err) {
      clearTimeout(timer)
      const errNode = err as NodeJS.ErrnoException
      if (errNode.code === 'ABORT_ERR' || errNode.name === 'AbortError') {
        // Timed out with some output — consider this a pass (server started but --help hung)
        console.log(`${CHECK} MCP server starts ... ${DIM}(timed out after 10s — process started)${RESET}`)
        passed++
      } else if (errNode.code === 'ENOENT') {
        console.log(`${CROSS} MCP server starts ... venv Python not found at ${venvPython}`)
        console.log(`  Run \`gsd-munch setup\` to provision the venv.`)
      } else {
        // Non-zero exit code from --help is still a "started" signal for many CLIs
        const exitCode = (err as { code?: number }).code
        if (typeof exitCode === 'number' && exitCode !== 0) {
          console.log(`${CHECK} MCP server starts ... ${DIM}(exited ${exitCode})${RESET}`)
          passed++
        } else {
          console.log(`${CROSS} MCP server starts ... ${errNode.message}`)
        }
      }
    }
  } else {
    console.log(`${CROSS} MCP server starts ... venv Python not found at ${venvPython}`)
    console.log(`  Run \`gsd-munch setup\` to provision the venv.`)
  }

  // Check 5: Grammar health (14 languages)
  total++
  if (venvPythonExists) {
    const checkScript = `
import sys
from tree_sitter_language_pack import get_language
langs = ${JSON.stringify(REQUIRED_LANGUAGES)}
ok = 0
failed = []
for lang in langs:
    try:
        get_language(lang)
        ok += 1
    except Exception:
        failed.append(lang)
print(f"{ok}/{len(langs)}")
if failed:
    print(",".join(failed))
sys.exit(0 if ok == len(langs) else 1)
`
    try {
      const { stdout } = await execFileAsync(
        venvPython,
        ['-c', checkScript],
        {
          timeout: 30_000,
          env: { ...process.env, PYTHONPATH: pythonModulePath },
        },
      )
      const lines = stdout.trim().split('\n')
      const countLine = lines[0] ?? ''
      console.log(`${CHECK} ${countLine} languages ready`)
      passed++
    } catch (err) {
      const errNode = err as NodeJS.ErrnoException & { stdout?: string }
      const stdoutStr = errNode.stdout ?? ''
      const lines = stdoutStr.trim().split('\n')
      const countLine = lines[0] ?? '0/14'
      const missingLine = lines[1] ?? ''
      console.log(`${CROSS} ${countLine} languages ready`)
      if (missingLine) {
        console.log(`  Missing: ${missingLine}`)
      }
      console.log(`  Run \`gsd-munch setup\` to reinstall grammars.`)
    }
  } else {
    console.log(`${CROSS} 0/14 languages ready ... venv not provisioned`)
    console.log(`  Run \`gsd-munch setup\` to reinstall grammars.`)
  }

  // Check 6: Index health (end-to-end)
  total++
  if (venvPythonExists) {
    const indexScript = `
import tempfile, os, sys, shutil
sys.path.insert(0, '${pythonModulePath.replace(/\\/g, '\\\\')}')
from jcodemunch_mcp.tools.index_folder import index_folder
from jcodemunch_mcp.tools.get_file_outline import get_file_outline
tmp = tempfile.mkdtemp()
idx = tempfile.mkdtemp()
try:
    test_file = os.path.join(tmp, 'test_health.py')
    with open(test_file, 'w') as f:
        f.write('def health_check():\\n    return True\\n')
    result = index_folder(tmp, use_ai_summaries=False, storage_path=idx)
    if not result.get('success'):
        print(f"index_folder failed: {result.get('error', 'unknown error')}", file=sys.stderr)
        sys.exit(1)
    repo = result.get('repo', '')
    outline = get_file_outline(repo, 'test_health.py', storage_path=idx)
    assert 'health_check' in str(outline), f'Symbol not found in outline: {outline}'
    print('ok')
finally:
    shutil.rmtree(tmp, ignore_errors=True)
    shutil.rmtree(idx, ignore_errors=True)
`
    try {
      await execFileAsync(
        venvPython,
        ['-c', indexScript],
        {
          timeout: 30_000,
          env: { ...process.env, PYTHONPATH: pythonModulePath, JCODEMUNCH_USE_AI_SUMMARIES: 'false' },
        },
      )
      console.log(`${CHECK} Index health ... passed`)
      passed++
    } catch (err) {
      const errNode = err as NodeJS.ErrnoException & { stderr?: string }
      const stderrStr = errNode.stderr ?? errNode.message ?? 'unknown error'
      const shortErr = stderrStr.split('\n').filter(Boolean).pop() ?? stderrStr
      console.log(`${CROSS} Index health ... ${shortErr}`)
      console.log(`  Run \`gsd-munch setup\` to reinstall dependencies.`)
    }
  } else {
    console.log(`${CROSS} Index health ... venv not provisioned`)
    console.log(`  Run \`gsd-munch setup\` to reinstall dependencies.`)
  }

  // --- GSD Workflow Asset Checks ---
  console.log('')
  console.log('GSD workflow assets:')

  // Check 7: Command stubs exist
  total++
  if (existsSync(COMMANDS_GM_DIR)) {
    const stubFiles = readdirSync(COMMANDS_GM_DIR).filter(f => f.endsWith('.md'))
    const stubCount = stubFiles.length
    if (stubCount >= 30) {
      console.log(`${CHECK} command stubs ... ${stubCount} stubs in ~/.claude/commands/gm/`)
      passed++
    } else if (stubCount === 0) {
      console.log(`${WARN} command stubs ... directory exists but no .md files found`)
    } else {
      console.log(`${WARN} command stubs ... only ${stubCount} stubs found (expected >= 30)`)
      passed++
    }
  } else {
    console.log(`${CROSS} command stubs ... ~/.claude/commands/gm/ not found`)
  }

  // Check 8: Stub @-references resolve
  total++
  if (existsSync(COMMANDS_GM_DIR)) {
    const stubFiles = readdirSync(COMMANDS_GM_DIR).filter(f => f.endsWith('.md'))
    const brokenRefs: string[] = []
    for (const file of stubFiles) {
      const content = readFileSync(join(COMMANDS_GM_DIR, file), 'utf-8')
      // Extract @/absolute/path references
      const refMatches = content.match(/@(\/[^\s\n"'`.,)}\]]+)/g) ?? []
      for (const ref of refMatches) {
        const refPath = ref.slice(1) // strip leading @
        if (!existsSync(refPath)) {
          brokenRefs.push(`${file}: ${ref}`)
        }
      }
    }
    if (brokenRefs.length === 0) {
      console.log(`${CHECK} stub @-references resolve ... all references valid`)
      passed++
    } else {
      console.log(`${CROSS} stub @-references resolve ... ${brokenRefs.length} broken reference(s):`)
      for (const ref of brokenRefs.slice(0, 5)) {
        console.log(`    ${ref}`)
      }
      if (brokenRefs.length > 5) {
        console.log(`    ... and ${brokenRefs.length - 5} more`)
      }
    }
  } else {
    console.log(`${CROSS} stub @-references resolve ... ~/.claude/commands/gm/ not found`)
  }

  // Check 9: Agent files exist
  total++
  if (existsSync(AGENTS_DIR)) {
    const agentFiles = readdirSync(AGENTS_DIR).filter(f => f.startsWith('gsd-') && f.endsWith('.md'))
    const agentCount = agentFiles.length
    if (agentCount >= 10) {
      console.log(`${CHECK} agent files ... ${agentCount} gsd-*.md files in ~/.claude/agents/`)
      passed++
    } else if (agentCount === 0) {
      console.log(`${WARN} agent files ... ~/.claude/agents/ exists but no gsd-*.md files found`)
    } else {
      console.log(`${WARN} agent files ... only ${agentCount} agent files found (expected >= 10)`)
      passed++
    }
  } else {
    console.log(`${WARN} agent files ... ~/.claude/agents/ directory not found`)
  }

  // Check 10: No stale get-shit-done references
  total++
  const staleRefs: string[] = []
  const dirsToCheck = [COMMANDS_GM_DIR, getGsdMunchHome()]
  for (const dir of dirsToCheck) {
    if (!existsSync(dir)) continue
    const checkDir = (dirPath: string) => {
      try {
        for (const entry of readdirSync(dirPath, { withFileTypes: true })) {
          const entryPath = join(dirPath, entry.name)
          if (entry.isDirectory()) {
            checkDir(entryPath)
          } else if (entry.isFile()) {
            try {
              const content = readFileSync(entryPath, 'utf-8')
              if (/\.claude\/get-shit-done\//.test(content)) {
                staleRefs.push(entryPath)
              }
            } catch {
              // Skip unreadable files
            }
          }
        }
      } catch {
        // Skip unreadable directories
      }
    }
    checkDir(dir)
  }
  if (staleRefs.length === 0) {
    console.log(`${CHECK} no stale get-shit-done references`)
    passed++
  } else {
    console.log(`${CROSS} stale get-shit-done references found in ${staleRefs.length} file(s):`)
    for (const ref of staleRefs.slice(0, 3)) {
      console.log(`    ${ref}`)
    }
    if (staleRefs.length > 3) {
      console.log(`    ... and ${staleRefs.length - 3} more`)
    }
  }

  // Check 11: gsd-tools.cjs callable
  total++
  const gsdToolsPath = join(getGsdMunchHome(), 'bin', 'gsd-tools.cjs')
  if (existsSync(gsdToolsPath)) {
    console.log(`${CHECK} gsd-tools.cjs present ... ${gsdToolsPath}`)
    passed++
  } else {
    console.log(`${CROSS} gsd-tools.cjs present ... not found at ${gsdToolsPath}`)
  }

  // Summary
  console.log('')
  console.log(`${passed} of ${total} checks passed`)
  if (passed < total) {
    console.log('Run `gsd-munch setup` to fix provisioning issues.')
  }
}
